import { useEffect, useRef } from 'react'
import { FaCheck, FaExclamation, FaTimes, FaStop, FaCompressAlt } from "react-icons/fa";
import { useAnalysisRun, isTerminalStatus } from '../context/AnalysisRunContext.jsx';

const PHASES = [
  { name: 'Preparing', icon: '1' },
  { name: 'SourceOptima AI', icon: '2' },
  { name: 'SourceOptima Database', icon: '3' },
  { name: 'Complete', icon: '4' },
]

const LEVEL_COLORS = {
  phase: 'text-[#f26522] font-bold',      // Orange for phase headers
  success: 'text-[#94c43d]',               // Accent green for success
  info: 'text-gray-400',                   // Gray for info
  warning: 'text-[#f26522]',               // Orange for warnings
  error: 'text-red-400',                   // Red for errors
}

// Full live-log modal for whichever run is currently open.
//
// A pure view over AnalysisRunContext: it owns no run state, so it can be
// unmounted freely (minimize, navigate, switch runs) while the run keeps
// streaming in the background. The header leads with the PROJECT FOLDER,
// because with several concurrent runs that is the only label that tells the
// user which analysis they're looking at.
export default function AnalysisProgressModal() {
  const {
    openRun: run,
    activeRuns,
    stopRun,
    completeRun,
    closeRun,
    goToAssembly,
    minimizeRun,
  } = useAnalysisRun()

  const logContainerRef = useRef(null)

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [run?.logs])

  if (!run) return null

  const {
    taskId, meta, status, progress, logs,
    completedResult, isStopping, startTime,
    pollInterval, concurrentUsers, isThrottled, pendingAssemblyId,
  } = run

  const folderName = meta?.folderName
  const playName = meta?.playName || 'Analysis'
  const fileCount = meta?.fileCount ?? 0
  const currentPhase = progress?.phase ?? 0
  const elapsedSeconds = progress?.elapsed_seconds ??
    Math.round((Date.now() - (startTime || Date.now())) / 1000)
  const terminal = isTerminalStatus(status)
  const displayLogs = logs
  // Other runs still going while this modal is open — minimizing returns to them.
  const otherActive = activeRuns.filter(r => r.taskId !== taskId).length

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f6f4ed] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-[#1e5045]/20">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e5045]/10 bg-white">
          <div className="min-w-0">
            {/* Folder first — disambiguates concurrent runs */}
            <h2 className="text-xl font-semibold text-[#1e5045] truncate" title={playName}>
              {playName || 'Analysis'}
            </h2>
            <p className="text-sm text-[#1e5045]/60 truncate" title={folderName}>
              {status === 'CANCELLED' ? 'Stopped · ' : ''}{folderName ? `${folderName} · ` : ''}{fileCount} file(s)
              {otherActive > 0 && (
                <span className="text-[#f26522]">
                  {' '}· {otherActive} other analysis{otherActive !== 1 ? 'es' : ''} running
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Minimize — collapses to the global indicator, which lists every run */}
            {!terminal && (
              <button
                onClick={minimizeRun}
                className="p-2 hover:bg-[#1e5045]/10 rounded-lg transition-colors"
                title="Minimize"
              >
                <FaCompressAlt className="w-4 h-4 text-[#1e5045]" />
              </button>
            )}
            {/* Stop Button - show when running */}
            {!terminal && (
              <button
                onClick={() => stopRun(taskId)}
                disabled={isStopping}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isStopping
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
              >
                <FaStop className="w-3 h-3" />
                {isStopping ? 'Stopping...' : 'Stop'}
              </button>
            )}
            {/* View Results — individual analysis done */}
            {completedResult && (
              <button
                onClick={() => completeRun(taskId)}
                className="px-4 py-2 bg-[#1e5045] text-white rounded-lg text-sm font-medium hover:bg-[#0d5d57] transition-colors"
              >
                View Results
              </button>
            )}
            {/* Assembly run that finished in the background — navigate on click */}
            {pendingAssemblyId && (
              <button
                onClick={() => goToAssembly(taskId)}
                className="px-4 py-2 bg-[#1e5045] text-white rounded-lg text-sm font-medium hover:bg-[#0d5d57] transition-colors"
              >
                Review BOM
              </button>
            )}
            {/* Close Button - show when finished or cancelled */}
            {terminal && (
              <button
                onClick={() => closeRun(taskId)}
                className="p-2 hover:bg-[#1e5045]/10 rounded-lg transition-colors"
              >
                <FaTimes className="w-4 h-4 text-[#1e5045]" />
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Phase Stepper */}
          <div className="flex flex-wrap gap-2">
            {PHASES.map((phase, idx) => {
              let bgColor = 'bg-[#1e5045]/10 text-[#1e5045]/50'
              let iconBg = 'bg-[#1e5045]/20'
              if (idx < currentPhase) {
                bgColor = 'bg-[#94c43d] text-white'
                iconBg = 'bg-white/30'
              } else if (idx === currentPhase) {
                bgColor = 'bg-[#f26522] text-white animate-pulse'
                iconBg = 'bg-white/20'
              }

              return (
                <div
                  key={phase.name}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${bgColor}`}
                >
                  <span className={`w-4 h-4 rounded-full ${iconBg} flex items-center justify-center text-[10px]`}>
                    {idx < currentPhase ? '✓' : phase.icon}
                  </span>
                  {phase.name}
                </div>
              )
            })}
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#1e5045]/70 truncate max-w-[70%]">
                {progress?.message || 'Preparing analysis...'}
              </span>
              <span className="font-medium text-[#f26522]">
                {typeof elapsedSeconds === 'number' ? elapsedSeconds.toFixed(1) : elapsedSeconds}s
              </span>
            </div>
            <div className="h-2 bg-[#1e5045]/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#f26522] to-[#94c43d] rounded-full transition-all duration-300"
                style={{ width: `${progress?.progress_pct || 5}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-[#1e5045]/50">
              <span>
                {progress?.current !== undefined && progress?.total !== undefined
                  ? `${progress.current} / ${progress.total} files`
                  : ''}
              </span>
              <span>{Math.round(progress?.progress_pct || 0)}%</span>
            </div>
          </div>

          {/* Activity Log - Terminal Style */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-[#1e5045]">Activity Log</h4>
              <span className="text-xs text-[#1e5045]/40">
                {displayLogs.length} entries
              </span>
            </div>
            <div
              ref={logContainerRef}
              className="bg-[#1e5045] rounded-lg p-3 h-64 overflow-y-auto font-mono text-xs"
            >
              {displayLogs.length > 0 ? (
                displayLogs.map((entry, idx) => (
                  <div
                    key={idx}
                    className={`py-0.5 ${LEVEL_COLORS[entry.level] || 'text-gray-400'} ${
                      entry.level === 'phase' ? 'mt-2 first:mt-0' : ''
                    }`}
                  >
                    <span className="text-[#f6f4ed]/40 mr-2">
                      [{typeof entry.timestamp === 'number'
                        ? entry.timestamp.toFixed(1).padStart(6)
                        : '  0.0'}s]
                    </span>
                    {entry.message}
                  </div>
                ))
              ) : (
                <div className="text-[#f6f4ed]/50 flex items-center gap-2">
                  <span className="animate-pulse">●</span>
                  Waiting for analysis to start...
                </div>
              )}
              {/* Throttle notification */}
              {isThrottled && !terminal && (
                <div className="py-0.5 text-[#f26522] mt-2">
                  <span className="text-[#f6f4ed]/40 mr-2">[SYSTEM]</span>
                  High server activity ({concurrentUsers} users) - updates slowed to every {(pollInterval / 1000).toFixed(1)}s
                </div>
              )}
              {/* Blinking cursor */}
              {!terminal && (
                <div className="text-[#94c43d] animate-pulse mt-1">▌</div>
              )}
            </div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center gap-2 text-sm">
            {status === 'SUCCESS' ? (
              <>
                <FaCheck className="w-5 h-5 text-[#94c43d]" />
                <span className="text-[#1e5045] font-medium">
                  {pendingAssemblyId ? (
                    <>Assembly complete! Click <strong>Review BOM</strong> to continue.</>
                  ) : (
                    <>Analysis complete! Click <strong>View Results</strong> to continue.</>
                  )}
                </span>
              </>
            ) : status === 'FAILURE' ? (
              <>
                <FaExclamation className="w-5 h-5 text-red-500" />
                <span className="text-red-600 font-medium">Analysis failed</span>
              </>
            ) : status === 'CANCELLED' ? (
              <>
                <FaExclamation className="w-5 h-5 text-[#f26522]" />
                <span className="text-[#1e5045] font-medium">Analysis stopped — cached results preserved</span>
              </>
            ) : (
              <>
                <div className="w-5 h-5 border-2 border-[#f26522] border-t-transparent rounded-full animate-spin" />
                <span className="text-[#1e5045]">
                  Running analysis via SourceOptima AI...
                  {progress?.successful !== undefined && (
                    <span className="ml-2 text-[#1e5045]/50">
                      ({progress.successful} successful
                      {progress.cached > 0 && `, ${progress.cached} cached`}
                      {progress.failed > 0 && `, ${progress.failed} failed`})
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
